import React, { createContext, useContext, useState, useEffect } from 'react';

const BookingContext = createContext(null);

export const BookingProvider = ({ children }) => {
  const [bookingData, setBookingData] = useState({
    apartment: null,
    checkIn: null,
    checkOut: null,
    guests: { adults: 1, children: 0 },
    pricing: null,
    guestInfo: null,
    specialRequests: ''
  });

  // Load booking data from localStorage on mount
  useEffect(() => {
    const savedBooking = localStorage.getItem('bookingData');
    if (savedBooking) {
      const parsed = JSON.parse(savedBooking);
      // Convert date strings back to Date objects
      if (parsed.checkIn) parsed.checkIn = new Date(parsed.checkIn);
      if (parsed.checkOut) parsed.checkOut = new Date(parsed.checkOut);
      setBookingData(parsed);
    }
  }, []);

  // Save booking data to localStorage on change
  useEffect(() => {
    if (bookingData.apartment) {
      localStorage.setItem('bookingData', JSON.stringify(bookingData));
    }
  }, [bookingData]);

  const setApartment = (apartment) => {
    setBookingData(prev => ({ ...prev, apartment }));
  };

  const setDates = (checkIn, checkOut) => {
    setBookingData(prev => ({ ...prev, checkIn, checkOut }));
  };

  const setGuests = (guests) => {
    setBookingData(prev => ({ ...prev, guests }));
  };

  const setPricing = (pricing) => {
    setBookingData(prev => ({ ...prev, pricing }));
  };

  const setGuestInfo = (guestInfo) => {
    setBookingData(prev => ({ ...prev, guestInfo }));
  };

  const setSpecialRequests = (specialRequests) => {
    setBookingData(prev => ({ ...prev, specialRequests }));
  };

  const calculateNights = () => {
    if (!bookingData.checkIn || !bookingData.checkOut) return 0;
    const diffTime = Math.abs(bookingData.checkOut - bookingData.checkIn);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getTotalGuests = () => {
    return (bookingData.guests?.adults || 0) + (bookingData.guests?.children || 0);
  };

  const clearBooking = () => {
    setBookingData({
      apartment: null,
      checkIn: null,
      checkOut: null,
      guests: { adults: 1, children: 0 },
      pricing: null,
      guestInfo: null,
      specialRequests: ''
    });
    localStorage.removeItem('bookingData');
  };

  const isBookingComplete = () => {
    return !!(
      bookingData.apartment &&
      bookingData.checkIn &&
      bookingData.checkOut &&
      bookingData.guests.adults > 0
    );
  };

  return (
    <BookingContext.Provider value={{
      bookingData,
      setBookingData,
      setApartment,
      setDates,
      setGuests,
      setPricing,
      setGuestInfo,
      setSpecialRequests,
      calculateNights,
      getTotalGuests,
      clearBooking,
      isBookingComplete
    }}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

export default BookingContext;
